// client/src/pages/secure/SocialMediaPage.js — Administration → Social Media (managing broker).
// Profile links (shown in the website footer), the posting switches, the connection status
// of each network, and the queue: write a post, approve it (now or at its time), edit or
// cancel. Every post is a draft until approved; Automatic posting starts off.
// Everything is read from and saved to /api/social (routes/social.js, lib/social.js).
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import PageHeader from '../../components/shared/PageHeader';

const LABEL = { facebook: 'Facebook', instagram: 'Instagram', x: 'X', wechat: 'WeChat' };
const STATUS_STYLE = {
    draft: 'bg-slate-100 text-slate-700',
    approved: 'bg-blue-100 text-blue-800',
    posting: 'bg-blue-100 text-blue-800',
    posted: 'bg-green-100 text-green-800',
    partly_posted: 'bg-amber-100 text-amber-900',
    failed: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-500',
};
const field = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm';
const btn = 'px-3 py-2 rounded-md text-sm font-medium';
const primary = `${btn} bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50`;
const plain = `${btn} border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50`;
const fmt = (d) => (d ? new Date(d).toLocaleString() : '');
const toLocalInput = (d) => {
    if (!d) return '';
    const t = new Date(d);
    const p = (n) => String(n).padStart(2, '0');
    return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}T${p(t.getHours())}:${p(t.getMinutes())}`;
};

const PROFILE_FIELDS = [
    { key: 'social_facebook_url', label: 'Facebook page link', placeholder: 'https://www.facebook.com/…' },
    { key: 'social_instagram_url', label: 'Instagram link', placeholder: 'https://www.instagram.com/…' },
    { key: 'social_x_url', label: 'X link', placeholder: 'https://x.com/…' },
    { key: 'social_wechat_id', label: 'WeChat ID', placeholder: 'WeChat ID or the linked phone number' },
    { key: 'social_wechat_phone', label: 'WeChat phone', placeholder: '773 205 5493' },
];

function Findings({ lint }) {
    if (!lint || !lint.findings || !lint.findings.length) return null;
    return (
        <ul className="mt-2 text-xs space-y-1">
            {lint.findings.map((f, i) => (
                <li key={i} className={f.severity === 'block' ? 'text-red-800' : 'text-amber-900'}>
                    <strong>{f.severity === 'block' ? 'Must fix' : 'Check'}:</strong> “{f.term}” — {f.why} {f.suggestion}
                </li>
            ))}
        </ul>
    );
}

function PostEditor({ initial, networks, onSave, onCancel, busy }) {
    const [p, setP] = useState(() => ({
        body: initial.body || '',
        link_url: initial.link_url || '',
        image_url: initial.image_url || '',
        scheduled_at: toLocalInput(initial.scheduled_at),
        networks: String(initial.networks || 'facebook,instagram,x').split(',').filter(Boolean),
    }));
    const toggle = (n) => setP((s) => ({ ...s, networks: s.networks.includes(n) ? s.networks.filter((x) => x !== n) : [...s.networks, n] }));
    return (
        <div className="space-y-3">
            <label className="block text-sm font-medium">Post
                <textarea className={`${field} mt-1 h-36`} value={p.body} onChange={(e) => setP({ ...p, body: e.target.value })} maxLength={2200}
                    placeholder="What to say. Include the brokerage name; routine posts add it for you." />
                <span className="text-xs text-slate-500">{p.body.length} / 2,200</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm font-medium">Link (optional)
                    <input className={`${field} mt-1`} value={p.link_url} onChange={(e) => setP({ ...p, link_url: e.target.value })} placeholder="https://…" />
                </label>
                <label className="block text-sm font-medium">Photo link (needed for Instagram)
                    <input className={`${field} mt-1`} value={p.image_url} onChange={(e) => setP({ ...p, image_url: e.target.value })} placeholder="https://…jpg" />
                </label>
                <label className="block text-sm font-medium">Post at (optional)
                    <input type="datetime-local" className={`${field} mt-1`} value={p.scheduled_at} onChange={(e) => setP({ ...p, scheduled_at: e.target.value })} />
                </label>
                <fieldset className="text-sm">
                    <legend className="font-medium">Networks</legend>
                    <div className="flex gap-4 mt-2">
                        {networks.map((n) => (
                            <label key={n} className="flex items-center gap-1.5">
                                <input type="checkbox" checked={p.networks.includes(n)} onChange={() => toggle(n)} /> {LABEL[n]}
                            </label>
                        ))}
                    </div>
                </fieldset>
            </div>
            <div className="flex gap-2">
                <button className={primary} disabled={busy} onClick={() => onSave({
                    ...p, networks: p.networks.join(','), scheduled_at: p.scheduled_at ? new Date(p.scheduled_at).toISOString() : null })}>Save draft</button>
                <button className={plain} onClick={onCancel}>Close</button>
            </div>
        </div>
    );
}

export default function SocialMediaPage() {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);     // null | 'new' | post
    const [filter, setFilter] = useState('');

    const load = useCallback(async () => {
        try {
            const d = await api.get(`/api/social${filter ? `?status=${filter}` : ''}`);
            setData(d);
            setForm(d.settings);
            setErr('');
        } catch (e) { setErr(e.message); }
    }, [api, filter]);
    useEffect(() => { load(); }, [load]);

    const run = async (fn, ok) => {
        setBusy(true); setMsg(''); setErr('');
        try { const r = await fn(); if (ok) setMsg(typeof ok === 'function' ? ok(r) : ok); await load(); return r; } catch (e) {
            setErr(e.message);
            if (e.payload && e.payload.errors) setErrors(e.payload.errors);
            return null;
        } finally { setBusy(false); }
    };

    const saveSettings = async (keys) => {
        setErrors({});
        const body = Object.fromEntries(keys.map((k) => [k, form[k] ?? '']));
        await run(() => api.put('/api/social/settings', body), 'Saved.');
    };
    const savePost = async (p) => {
        const r = editing === 'new'
            ? await run(() => api.post('/api/social/posts', p), 'Draft saved. Approve it when you are happy with it.')
            : await run(() => api.put(`/api/social/posts/${editing.post_id}`, p), 'Saved. It is a draft again until you approve it.');
        if (r) setEditing(null);
    };
    const approve = (post, now) => run(() => api.post(`/api/social/posts/${post.post_id}/approve`, { now }),
        (r) => (now ? `Result: ${Object.entries(r.results || {}).map(([n, x]) => `${LABEL[n]} — ${x.message}`).join(' ')}` : `Approved; it posts at ${fmt(r.scheduled_at)} (the next daily run after that time).`));
    const cancel = (post) => { if (window.confirm('Cancel this post? It stays in the list as cancelled.')) run(() => api.post(`/api/social/posts/${post.post_id}/cancel`, {}), 'Cancelled.'); };

    if (!data) return <div className="p-6">{err || 'Loading…'}</div>;
    const c = data.connections || {};
    const switchOn = (k) => String(form[k]) === '1';

    return (
        <div className="space-y-6">
            <PageHeader title="Social Media" description="Your Facebook, Instagram, X and WeChat: the links on the website, routine posts and the posting queue. Nothing is posted until you approve it, unless you switch on Automatic posting."
                actions={<button className={primary} onClick={() => setEditing('new')}>New post</button>} />
            {msg && <div className="p-3 rounded-md bg-green-50 text-green-800 text-sm">{msg}</div>}
            {err && <div className="p-3 rounded-md bg-red-50 text-red-800 text-sm">{err}</div>}

            {editing && (
                <section className="bg-white p-4 sm:p-6 rounded-lg shadow">
                    <h2 className="text-lg font-semibold mb-3">{editing === 'new' ? 'New post' : `Edit post #${editing.post_id}`}</h2>
                    <PostEditor initial={editing === 'new' ? { networks: form.social_post_networks } : editing} networks={data.networks} onSave={savePost} onCancel={() => setEditing(null)} busy={busy} />
                </section>
            )}

            <section className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <h2 className="text-lg font-semibold">Connections</h2>
                <p className="text-sm text-slate-600 mt-1">Posting needs each network's keys, entered at <Link to="/dashboard/integrations" className="underline">Administration → Integrations</Link> (Facebook &amp; Instagram, X). WeChat has no posting connection — an Official Account is needed — so it is shown on the website as your WeChat ID.</p>
                <ul className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                    {['facebook', 'instagram', 'x', 'wechat'].map((n) => (
                        <li key={n} className={`rounded-md px-3 py-2 ${c[n] ? 'bg-green-50 text-green-800' : 'bg-slate-50 text-slate-600'}`}>
                            <strong>{LABEL[n]}</strong>: {c[n] ? 'connected' : n === 'wechat' ? 'profile only' : 'not connected'}
                        </li>
                    ))}
                </ul>
            </section>

            <section className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <h2 className="text-lg font-semibold">Profiles on the website</h2>
                <p className="text-sm text-slate-600 mt-1">Shown in the website footer and given to search engines as the brokerage's official accounts. Leave a box empty to hide it.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    {PROFILE_FIELDS.map((f) => (
                        <label key={f.key} className="block text-sm font-medium">{f.label}
                            <input className={`${field} mt-1`} value={form[f.key] || ''} placeholder={f.placeholder} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                            {errors[f.key] && <span className="text-xs text-red-700">This {errors[f.key]}.</span>}
                        </label>
                    ))}
                </div>
                <button className={`${primary} mt-4`} disabled={busy} onClick={() => saveSettings(PROFILE_FIELDS.map((f) => f.key))}>Save profiles</button>
            </section>

            <section className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <h2 className="text-lg font-semibold">Routine posts</h2>
                <div className="space-y-3 mt-3 text-sm">
                    <label className="flex items-start gap-2">
                        <input type="checkbox" className="mt-1" checked={switchOn('social_routine_listings')} onChange={(e) => setForm({ ...form, social_routine_listings: e.target.checked ? '1' : '0' })} />
                        <span><strong>Draft a post for each new BREC listing</strong> for sale or lease (the morning run; one a day, never other brokers' MLS listings).</span>
                    </label>
                    <label className="flex items-start gap-2">
                        <input type="checkbox" className="mt-1" checked={switchOn('social_auto_post')} onChange={(e) => setForm({ ...form, social_auto_post: e.target.checked ? '1' : '0' })} />
                        <span><strong>Automatic posting</strong> — routine posts that pass the fair-housing check are approved without you. Off: every post waits for your approval.</span>
                    </label>
                    <div className="flex flex-wrap gap-4 items-end">
                        <label className="block">Post at hour (0–23)
                            <input type="number" min="0" max="23" className={`${field} mt-1 w-24`} value={form.social_post_hour || ''} onChange={(e) => setForm({ ...form, social_post_hour: e.target.value })} />
                        </label>
                        <fieldset>
                            <legend>Routine posts go to</legend>
                            <div className="flex gap-4 mt-2">
                                {data.networks.map((n) => {
                                    const list = String(form.social_post_networks || '').split(',').filter(Boolean);
                                    return (
                                        <label key={n} className="flex items-center gap-1.5">
                                            <input type="checkbox" checked={list.includes(n)} onChange={() => setForm({ ...form, social_post_networks: (list.includes(n) ? list.filter((x) => x !== n) : [...list, n]).join(',') })} /> {LABEL[n]}
                                        </label>
                                    );
                                })}
                            </div>
                        </fieldset>
                    </div>
                    {Object.keys(errors).filter((k) => !k.startsWith('social_') || !PROFILE_FIELDS.some((f) => f.key === k)).map((k) => <p key={k} className="text-xs text-red-700">{k.replace('social_', '').replace(/_/g, ' ')}: {errors[k]}</p>)}
                    <div className="flex flex-wrap gap-2">
                        <button className={primary} disabled={busy} onClick={() => saveSettings(['social_routine_listings', 'social_auto_post', 'social_post_hour', 'social_post_networks'])}>Save switches</button>
                        <button className={plain} disabled={busy} onClick={() => run(() => api.post('/api/social/routine/run', {}), (r) => r.message)}>Draft listing posts now</button>
                    </div>
                </div>
            </section>

            <section className="bg-white p-4 sm:p-6 rounded-lg shadow">
                <div className="flex flex-wrap gap-3 justify-between items-center">
                    <h2 className="text-lg font-semibold">Posts</h2>
                    <select className={`${field} w-auto`} value={filter} onChange={(e) => setFilter(e.target.value)}>
                        <option value="">All</option>
                        {Object.keys(STATUS_STYLE).map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                </div>
                {!data.posts.length && <p className="text-sm text-slate-500 mt-3">No posts yet. Write one with New post, or press Draft listing posts now.</p>}
                <ul className="divide-y mt-3">
                    {data.posts.map((p) => (
                        <li key={p.post_id} className="py-4">
                            <div className="flex flex-wrap gap-2 items-center text-xs">
                                <span className={`px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[p.status] || ''}`}>{p.status.replace('_', ' ')}</span>
                                <span className="text-slate-500">#{p.post_id} · {p.source_type === 'listing' ? 'routine (listing)' : 'written by hand'} · {String(p.networks).split(',').map((n) => LABEL[n]).join(', ')}</span>
                                {p.scheduled_at && p.status !== 'posted' && <span className="text-slate-500">· posts at {fmt(p.scheduled_at)}</span>}
                                {p.posted_at && <span className="text-slate-500">· posted {fmt(p.posted_at)}</span>}
                            </div>
                            <p className="mt-2 text-sm whitespace-pre-wrap break-words">{p.body}</p>
                            {p.image_url && <p className="text-xs text-slate-500 mt-1 break-all">Photo: {p.image_url}</p>}
                            {p.x_preview && p.x_preview !== p.body && <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap"><strong>On X (280 characters):</strong> {p.x_preview}</p>}
                            <Findings lint={p.lint} />
                            {p.results && (
                                <ul className="mt-2 text-xs">
                                    {Object.entries(p.results).map(([n, r]) => <li key={n} className={r.ok ? 'text-green-800' : 'text-red-800'}>{LABEL[n]}: {r.message}</li>)}
                                </ul>
                            )}
                            {['draft', 'approved', 'failed'].includes(p.status) && (
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {p.status !== 'approved' && <button className={primary} disabled={busy} onClick={() => approve(p, false)}>Approve</button>}
                                    {p.status !== 'approved' && <button className={plain} disabled={busy} onClick={() => approve(p, true)}>Approve and post now</button>}
                                    <button className={plain} disabled={busy} onClick={() => setEditing(p)}>Edit</button>
                                    <button className={plain} disabled={busy} onClick={() => cancel(p)}>Cancel</button>
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
}
